import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInterview } from "../../contexts/interview.context";
import { InterViewCurrentTask, InterviewInitState } from "../../types/interview.type";
import { useAutoPlayVideo } from "./VideoRestarter";
import { SessionStorage } from "../../types/general.type";

enum CurrentVideo {
    IDLE = "IDLE",
    TALKING = "TALKING",
    SCORING = "SCORING",
}
const InterviewVideo = () => {

    const navigate = useNavigate();
    const { interview, } = useInterview();
    const [interviewStarted, setInterviewStarted] = useState(false);

    const { currentTask, interviewData, setUp, audioRef } = interview;
    const { isProvidingScore } = interviewData || {};
    const isInterviewCompleted = setUp?.completed;

    // const location = useLocation();
    const state = JSON.parse(sessionStorage.getItem(SessionStorage.INTERVIEW_STATE) || "{}") as InterviewInitState;
    
    useEffect(() => {
        if (state?.selectedInterviweer?.voiceName == null || 
            state?.selectedInterviweer?.image == null) 
        {
            navigate("/interview-setup");
        }
    }, [navigate])

    const voiceName = state?.selectedInterviweer?.voiceName;
    const interviewerProfilePicture = state?.selectedInterviweer?.image || "./logo/green-logo.png";
    const idleVideo = `/real_videos/${voiceName}_idel.mp4`
    const talkingVideo = `/real_videos/${voiceName}_talking.mp4`
    const scoringVideo = `/real_videos/${voiceName}_scoring.mp4`

    // Target video driven by interview state
    const [currentVideo, setCurrentVideo] = useState(CurrentVideo.IDLE);
    // Actually displayed video switches only after next video has a frame ready
    const [displayedVideo, setDisplayedVideo] = useState(CurrentVideo.IDLE);

    const talkingVideoRef = useRef<HTMLVideoElement | null>(null);
    const idleVideoRef = useRef<HTMLVideoElement | null>(null);
    const scoringVideoRef = useRef<HTMLVideoElement | null>(null);
    
    

    // Restart videos when the component mounts (visibility handling)
    useAutoPlayVideo([talkingVideoRef, idleVideoRef, scoringVideoRef]);

    // Update target video when audio state or score flag changes
    useEffect(() => {
        const isBotSpeking = currentTask === InterViewCurrentTask.BOT_SPEAKING;
        const isYouSpeaking = currentTask === InterViewCurrentTask.YOU_SPEAKING;
        if (isYouSpeaking) {
            try {
                if (idleVideoRef.current) {
                    if (idleVideoRef.current.paused || idleVideoRef.current.ended)
                        idleVideoRef.current.play();
                    idleVideoRef.current.loop = true;
                    idleVideoRef.current.muted = true;
                }
            } catch (error) {

            }
        }

        if (isBotSpeking && !isProvidingScore) {
            setInterviewStarted(true);
            setCurrentVideo(CurrentVideo.TALKING);
        }
        else if (isBotSpeking && isProvidingScore) {
            setCurrentVideo(CurrentVideo.SCORING);
        } else {
            setCurrentVideo(CurrentVideo.IDLE);
        }
    }, [currentTask, isProvidingScore]);

    // Ensure only the active video plays; pause the rest
    const pauseOthers = (except?: HTMLVideoElement | null) => {
        [talkingVideoRef.current, scoringVideoRef.current, idleVideoRef.current].forEach(v => {
            if (v && v !== except) {
                try { v.pause(); } catch { }
            }
        });
    };

    // Seamless switching: wait until the target video has a frame ready before showing it
    useEffect(() => {
        const selectRef = (cv: CurrentVideo): HTMLVideoElement | null => {
            if (cv === CurrentVideo.TALKING) return talkingVideoRef.current;
            if (cv === CurrentVideo.SCORING) return scoringVideoRef.current;
            return idleVideoRef.current;
        };

        const target = selectRef(currentVideo);
        if (!target) return;

        // Prime playback for the next video
        const primeAndShow = async () => {
            try {
                target.muted = true; // for mobile autoplay safety
                // Attempt play to force decode/buffer
                const playPromise = target.play();
                if (playPromise) {
                    await playPromise.catch(() => { /* ignore */ });
                }

                pauseOthers(target);

                // If requestVideoFrameCallback is available, wait for first rendered frame
                const useRVFC = (target as any).requestVideoFrameCallback && typeof (target as any).requestVideoFrameCallback === 'function';

                if (useRVFC) {
                    let called = false;
                    const rvfc = (target as any).requestVideoFrameCallback((now: number, metadata: any) => {
                        called = true;
                        setDisplayedVideo(currentVideo);
                    });
                    // Fallback timer in case rvfc doesn't fire promptly
                    setTimeout(() => {
                        if (!called) setDisplayedVideo(currentVideo);
                    }, 120);
                    return;
                }

                // Fallback: use readyState/events
                if (target.readyState >= 2) {
                    setDisplayedVideo(currentVideo);
                    return;
                }
                const onLoaded = () => {
                    setDisplayedVideo(currentVideo);
                    target.removeEventListener('loadeddata', onLoaded);
                    target.removeEventListener('canplay', onLoaded);
                };
                target.addEventListener('loadeddata', onLoaded);
                target.addEventListener('canplay', onLoaded);
            } catch (e) {
                setDisplayedVideo(currentVideo);
            }
        };

        primeAndShow();
    }, [currentVideo]);

    useEffect(() => {
        if (isInterviewCompleted) {
            idleVideoRef.current?.pause()
        }
    }, [isInterviewCompleted])

    // Keep talking video aligned with audio playback where possible
    useEffect(() => {
        const audio = audioRef?.current;
        if (!audio) return;
        const onPlay = () => {
            if (talkingVideoRef.current) {
                try {
                    talkingVideoRef.current.currentTime = 0;
                    talkingVideoRef.current.play();
                } catch { }
            }
        };
        const onEnded = () => {
            // When voice ends, switch away from talking unless scoring
            if (!isProvidingScore) setCurrentVideo(CurrentVideo.IDLE);
        };
        audio.addEventListener('play', onPlay);
        audio.addEventListener('ended', onEnded);
        return () => {
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('ended', onEnded);
        };
    }, [audioRef, isProvidingScore]);



    return (
        <div className="p-2 relative smp-4 w-full h-[98%] max-w-xs min-w-[300px] max-h-[25vh] lg:max-h-[30vh] lg:max-w-[400px] aspect-video shadow-md flex flex-col items-center justify-center rounded-xl ">
            {
                isInterviewCompleted
                    ?
                    <div className='flex'>
                        <p className="dark:text-gray-400 text-slate-600">Interview completed</p>
                    </div>
                    :
                    interviewStarted
                        ?
                        (
                            <>
                                <video
                                    ref={talkingVideoRef}
                                    autoPlay
                                    loop
                                    muted
                                    preload="auto"
                                    playsInline
                                    disablePictureInPicture
                                    controls={false}
                                    controlsList="nodownload noplaybackrate noremoteplayback"
                                    onContextMenu={(e) => e.preventDefault()}
                                    className={`w-full absolute top-[50%] left-[50%] object-top  h-full object-cover rounded-md  ${displayedVideo === CurrentVideo.TALKING ? "block" : "hidden"} `}
                                    style={{
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                >
                                    <source src={talkingVideo} type="video/mp4" />

                                </video>
                                <video
                                    ref={scoringVideoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    loop
                                    preload="auto"
                                    disablePictureInPicture
                                    controls={false}
                                    controlsList="nodownload noplaybackrate noremoteplayback"
                                    onContextMenu={(e) => e.preventDefault()}
                                    className={`w-full absolute top-[50%] left-[50%] object-top h-full object-cover rounded-md  ${displayedVideo === CurrentVideo.SCORING ? "block" : "hidden"}  `}
                                    style={{
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                >
                                    <source src={scoringVideo} type="video/mp4" />
                                </video>

                                <video
                                    ref={idleVideoRef}
                                    src={idleVideo}
                                    loop
                                    autoPlay
                                    muted
                                    preload="auto"
                                    playsInline
                                    disablePictureInPicture
                                    controls={false}
                                    controlsList="nodownload noplaybackrate noremoteplayback"
                                    onContextMenu={(e) => e.preventDefault()}
                                    className={`w-full absolute top-[50%] left-[50%]  object-top h-full object-cover rounded-md  ${displayedVideo === CurrentVideo.IDLE ? "block" : "hidden"}  `}
                                    style={{
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                >
                                    <source src={idleVideo} type="video/mp4" />
                                </video>
                            </>
                        )
                        :
                        (
                            <div className="flex items-center justify-center gap-2 h-full w-full">
                                <img src={interviewerProfilePicture} alt="LOGO" className="w-24 h-24 border border-accent-500 rounded-full cursor-pointer flex-shrink-0" />
                                {/* <p className="text-gray-500">Waiting for  interviewer...</p> */}
                            </div>
                        )
            }

        </div>
    )
};

export default InterviewVideo;